import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import type { Match, Team } from '@brochure/shared'
import { LocalizedLink } from '@/components/LocalizedLink'
import { SideMark } from './SideMark'
import { cn } from '@/lib/utils'
import { ScoreFlaps } from './ScoreFlaps'
import { ScrollingName } from '@/components/ui/ScrollingName'

type MatchCardProps = {
  match: Match
  teams: Team[]
  tournamentId: string
  /** `night` nella striscia "In campo ora": riga d'inchiostro come il tabellone della home. */
  tone?: 'paper' | 'night'
  /** Falso per le copie (striscia live): il `data-testid` resta unico per partita. */
  withTestId?: boolean
  /** Numero d'ordine della partita nel girone, dentro la riga a sinistra. */
  number?: number
}

/**
 * Riga di una partita a tre colonne (casa, punteggio, trasferta): il punteggio sta sulle palette del tabellone,
 * in una casella di larghezza fissa così le righe restano allineate. Toccabile verso la pagina partita solo
 * se in corso o conclusa; la partita live ha il bordo corallo e il pallino.
 */
export function MatchCard({ match, teams, tournamentId, tone = 'paper', withTestId = true, number }: MatchCardProps) {
  const { t } = useTranslation()
  const nameOf = (teamId: string) => teams.find((team) => team.id === teamId)?.name ?? t('stoccoPong.bracket.tbd')
  const hasScore = match.homeScore !== null && match.awayScore !== null
  const isLive = match.status === 'live'
  const homeWon = match.status === 'completed' && hasScore && match.homeScore! > match.awayScore!
  const awayWon = match.status === 'completed' && hasScore && match.awayScore! > match.homeScore!
  const clickable = match.status === 'live' || match.status === 'completed'
  const isNight = tone === 'night'

  const nameClass = (won: boolean) =>
    cn('min-w-0 flex-1 font-body text-sm sm:text-base', won ? 'font-semibold' : '', isNight ? 'text-paper' : won || isLive ? 'text-ink' : 'text-ink-soft')

  const content = (
    <>
      {number !== undefined && (
        <span
          className={cn(
            'w-6 shrink-0 border-r pr-2 text-center font-body text-xs font-semibold tabular-nums',
            isNight ? 'border-paper/15 text-paper/60' : 'border-line text-ink-soft',
          )}
        >
          {number}
        </span>
      )}
      <SideMark side="home" />
      <ScrollingName className={cn(nameClass(homeWon), 'text-right')}>{nameOf(match.homeTeamId)}</ScrollingName>
      <span className="relative flex w-[5.5rem] shrink-0 flex-col items-center justify-center rounded-lg bg-ink py-1.5 text-paper">
        {isLive && (
          <span className="absolute -top-2 flex items-center gap-1 rounded-full bg-coral px-1.5 py-px font-body text-[0.6rem] font-bold uppercase tracking-wider text-ink">
            <span className="size-1 rounded-full bg-ink motion-safe:animate-pulse" aria-hidden="true" />
            {t('stoccoPong.live')}
          </span>
        )}
        <ScoreFlaps home={hasScore ? match.homeScore : null} away={hasScore ? match.awayScore : null} className="text-lg" />
      </span>
      <ScrollingName className={cn(nameClass(awayWon), 'text-left')}>{nameOf(match.awayTeamId)}</ScrollingName>
      <SideMark side="away" />
      {clickable ? (
        <ChevronRight aria-hidden="true" className={cn('size-4 shrink-0', isNight ? 'text-paper/50' : 'text-ink-soft')} />
      ) : (
        <span aria-hidden="true" className="size-4 shrink-0" />
      )}
    </>
  )

  const className = cn(
    'flex min-h-14 items-center gap-2.5 rounded-xl border px-3 py-2',
    isNight ? 'border-paper/15 bg-paper/5' : 'border-line bg-paper-deep/60',
    isLive && !isNight && 'border-coral bg-paper shadow-[3px_3px_0_var(--color-coral)]',
  )
  const testId = withTestId ? `match-${match.id}` : undefined

  if (clickable) {
    return (
      <LocalizedLink
        to="stoccoPongMatch"
        params={{ id: tournamentId, matchId: match.id }}
        data-testid={testId}
        className={cn(className, 'transition-[transform,border-color] duration-200 ease-out-expo active:scale-[0.99]', isNight ? 'hover:border-paper/40' : 'hover:border-ink')}
      >
        {content}
      </LocalizedLink>
    )
  }
  return (
    <div data-testid={testId} className={className}>
      {content}
    </div>
  )
}
